import { useEffect, useState } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useEventContext } from '@providers/EventProvider';

/**
 * True while the user is actually looking at the world — false for as long as a loader owns the
 * screen, whether that is the first entry or a reconnect.
 *
 * `isEventRun` is the hub's provider-agnostic "the world is running": myta-streams raises it off
 * `playStream` (`PixelStreamingWrapper`), GameCast off the teleport that retires its loader. Paired
 * with a resolved room it is the same condition `PixelStreamingWrapper` keeps the loading cinema up
 * for — and it is latched here for the same reason it is latched there: `currentRoom` is derived
 * state that blanks for a beat during the entry handshake and a seat move, and that gap is not the
 * loading screen coming back.
 *
 * `reconnecting` is myta-streams only (no other provider raises it), and is deliberately NOT
 * latched: the picture this page was showing is gone and the loader is back over it.
 */
export const useWorldOnScreen = (): boolean => {
  const { currentRoom, mytaStreams } = useMytaverseEvent() || {};
  const { isEventRun } = useEventContext();

  const [everShown, setEverShown] = useState(false);

  useEffect(() => {
    if (isEventRun && currentRoom) {
      setEverShown(true);
    }
  }, [isEventRun, currentRoom]);

  return everShown && !mytaStreams?.reconnecting;
};
